import apiClient from './apiClient';

/**
 * Provider codes are master data (User Story 92), so this is a plain string —
 * `aws`/`azure`/`gcp` are only the seeded defaults. Labels come from
 * `useCloudProviders`, never from a constant in this file.
 */
export type CloudProvider = string;
export type CloudProviderState =
  | 'running'
  | 'stopped'
  | 'terminated'
  | 'unknown';

export interface CloudProviderOption {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

/**
 * Environment tier master (User Story 95). Backed by the shared `lookups`
 * table, so it carries label/value rather than the provider's code/name —
 * `value` is the string stored on resources and environments.
 */
export interface CloudTier {
  id: number;
  label: string;
  value: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
}

export const CLOUD_PROVIDER_STATE_LABELS: Record<CloudProviderState, string> = {
  running: 'Running',
  stopped: 'Stopped',
  terminated: 'Terminated',
  unknown: 'Unknown',
};

export interface CloudResourceType {
  id: number;
  category: string;
  normalizedType: string;
  /** Native name per provider, keyed by `CloudProviderOption.code` — e.g. `{ aws: 'EC2' }`. */
  providerTypeNames: Record<string, string>;
  sortOrder: number;
  isActive: boolean;
}

export interface CloudEnvironment {
  id: number;
  provider: CloudProvider;
  name: string;
  providerAccountId: string;
  environmentTier?: string;
  ownerId?: number;
  owner?: { id: number; firstName: string; lastName: string; email: string };
  isActive: boolean;
  notes?: string;
  /** Computed server-side, scoped to what the caller may see. */
  resourceCount?: number;
  monthlyCost?: number;
}

export interface CloudResource {
  id: number;
  provider: CloudProvider;
  providerResourceId: string;
  providerResourceType?: string;
  resourceTypeId?: number;
  resourceType?: CloudResourceType;
  name: string;
  environmentId?: number;
  environment?: CloudEnvironment;
  region?: string;
  availabilityZone?: string;
  providerState: CloudProviderState;
  departmentId?: number;
  department?: { id: number; name: string };
  businessOwnerId?: number;
  businessOwner?: { id: number; firstName: string; lastName: string };
  technicalOwnerId?: number;
  technicalOwner?: { id: number; firstName: string; lastName: string };
  /** Who asked for this resource and who authorised it (User Story 96). */
  requestedById?: number;
  requestedBy?: { id: number; firstName: string; lastName: string };
  approvedById?: number;
  approvedBy?: { id: number; firstName: string; lastName: string };
  /** Prod / staging / dev tag — distinct from the `environment` record above. */
  environmentTier?: string;
  application?: string;
  costCenter?: string;
  /** Who invoices this resource (User Story 103) — not the provider running it. */
  vendorId?: number | null;
  vendor?: { id: number; name: string };
  lifecycleStatus: string;
  complianceStatus?: string;
  monthlyCost?: number | string;
  currency: string;
  /** When the cost figure was last typed — the only freshness signal there is. */
  costUpdatedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CloudResourceHistoryEntry {
  id: number;
  field: string;
  oldValue?: string;
  newValue?: string;
  source: string;
  changedBy?: { id: number; firstName: string; lastName: string };
  changedAt: string;
}

export interface CloudResourceQuery {
  search?: string;
  provider?: string[];
  environmentId?: number[];
  resourceTypeId?: number[];
  region?: string[];
  environmentTier?: string[];
  providerState?: string[];
  lifecycleStatus?: string[];
  departmentId?: number[];
  ownerId?: number;
  unassignedOnly?: string;
  sortBy?: string;
  sortDir?: 'ASC' | 'DESC';
  page?: number;
  limit?: number;
}

export interface CloudStatistics {
  total: number;
  active: number;
  stopped: number;
  unassigned: number;
  environments: number;
}

export interface CloudImportError {
  row: number;
  field: string;
  message: string;
}

export interface CloudImportValidation {
  rows: any[];
  errors: CloudImportError[];
  summary: {
    total: number;
    valid: number;
    invalid: number;
    toCreate: number;
    toUpdate: number;
  };
}

export interface CloudCostBreakdownRow {
  key: string;
  label: string;
  resourceCount: number;
  monthlyCost: number;
}

export interface CloudCostSummary {
  baseCurrency: string;
  totalMonthlyCost: number;
  resourcesWithCost: number;
  resourcesWithoutCost: number;
  /** Oldest cost timestamp in the set — the honest freshness of the total. */
  oldestCostUpdatedAt: string | null;
  newestCostUpdatedAt: string | null;
  staleCount: number;
  unconvertibleCurrencies: string[];
  unconvertibleResourceCount: number;
  byProvider: CloudCostBreakdownRow[];
  byEnvironment: CloudCostBreakdownRow[];
  byDepartment: CloudCostBreakdownRow[];
  byResourceType: CloudCostBreakdownRow[];
}

export interface CloudOverview {
  kpis: {
    totalResources: number;
    running: number;
    stoppedOrTerminated: number;
    unassigned: number;
    cloudEnvironments: number;
    monthlyCost: number;
    baseCurrency: string;
    compliancePercent: number;
    costAsOf: string | null;
    staleCostCount: number;
    unconvertibleCurrencies: string[];
  };
  byProvider: { label: string; count: number }[];
  byResourceType: { label: string; count: number }[];
  byDepartment: { label: string; count: number }[];
  byLifecycleStatus: { label: string; count: number }[];
}

/* ── Analytics → Cloud tab (User Story 98) ─────────────────────────────────
   Mirrors `backend/src/modules/cloud/cloud-analytics.service.ts`. Every count
   and amount is already scoped server-side to the caller's permission tier. */

export interface CloudAnalyticsBreakdownRow {
  key: string;
  label: string;
  count: number;
  /** Base-currency monthly cost of the resources in this slice. */
  monthlyCost: number;
}

export interface CloudAnalyticsMonthPoint {
  month: string;
  count: number;
  cumulative: number;
}

export interface CloudAnalyticsCostPoint {
  month: string;
  monthlyCost: number;
  resourcesWithCost: number;
}

export interface CloudAnalyticsTopResource {
  id: number;
  name: string;
  provider: string;
  resourceType: string | null;
  environment: string | null;
  department: string | null;
  environmentTier: string | null;
  providerState: string;
  monthlyCost: number;
  currency: string;
  monthlyCostBase: number;
}

export interface CloudAnalyticsRiskRow {
  severity: 'high' | 'medium' | 'low';
  findingCount: number;
  resourceCount: number;
}

export interface CloudAnalyticsUnavailableMetric {
  id: string;
  label: string;
  reason: string;
}

/**
 * One resource reduced to the keys the tab groups by (User Story 100).
 * Cross-filtering re-derives every visual from these rows in the browser —
 * pre-aggregated breakdowns alone cannot answer "which regions were AWS?".
 */
export interface CloudAnalyticsFact {
  id: number;
  name: string;
  provider: string;
  providerLabel: string;
  tier: string;
  tierLabel: string;
  environment: string;
  environmentLabel: string;
  resourceType: string;
  resourceTypeLabel: string;
  region: string;
  regionLabel: string;
  state: string;
  stateLabel: string;
  lifecycle: string;
  department: string;
  departmentLabel: string;
  owner: string;
  ownerLabel: string;
  monthlyCost: number | null;
  currency: string;
  monthlyCostBase: number | null;
  costUpdatedAt: string | null;
  staleCost: boolean;
  /** Registration date, ISO — backs the Cloud tab's date-range filter. */
  createdAt: string | null;
  createdMonth: string | null;
  createdBeforeWindow: boolean;
  costByMonth: (number | null)[];
  findings: string[];
}

export interface CloudAnalytics {
  baseCurrency: string;
  kpis: {
    totalResources: number;
    activeResources: number;
    inactiveResources: number;
    running: number;
    stopped: number;
    terminated: number;
    unknownState: number;
    unownedResources: number;
    distinctRegions: number;
    distinctProviders: number;
    monthlyCost: number;
    resourcesWithoutCost: number;
    compliancePercent: number;
    compliantResources: number;
    nonCompliantResources: number;
    idleCandidates: number;
    idleMonthlyCost: number;
    costAsOf: string | null;
    staleCostCount: number;
    unconvertibleCurrencies: string[];
  };
  byProvider: CloudAnalyticsBreakdownRow[];
  byEnvironmentTier: CloudAnalyticsBreakdownRow[];
  byEnvironment: CloudAnalyticsBreakdownRow[];
  byResourceType: CloudAnalyticsBreakdownRow[];
  byRegion: CloudAnalyticsBreakdownRow[];
  byProviderState: CloudAnalyticsBreakdownRow[];
  byLifecycleStatus: CloudAnalyticsBreakdownRow[];
  byDepartment: CloudAnalyticsBreakdownRow[];
  byOwner: CloudAnalyticsBreakdownRow[];
  createdOverTime: CloudAnalyticsMonthPoint[];
  costTrend: CloudAnalyticsCostPoint[];
  costTrendReconstructed: boolean;
  topCostlyResources: CloudAnalyticsTopResource[];
  riskBySeverity: CloudAnalyticsRiskRow[];
  idleResources: CloudAnalyticsTopResource[];
  unavailableMetrics: CloudAnalyticsUnavailableMetric[];
  /** The 12 YYYY-MM buckets `costByMonth` and `createdOverTime` are indexed by. */
  trendMonths: string[];
  findingCatalog: { id: string; title: string; severity: string }[];
  /** Empty when the scoped set is too large to ship; see `factsTruncated`. */
  facts: CloudAnalyticsFact[];
  factsTruncated: boolean;
}

export interface CloudAuditFinding {
  id: string;
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  count: number;
  resources: {
    id: number;
    name: string;
    provider: string;
    providerResourceId: string;
    environmentName: string | null;
    departmentName: string | null;
  }[];
}

export interface CloudAuditReport {
  totalResources: number;
  compliantResources: number;
  compliancePercent: number;
  findings: CloudAuditFinding[];
}

export type CloudRecommendationState =
  | 'open'
  | 'acknowledged'
  | 'in_progress'
  | 'actioned'
  | 'dismissed';

export const RECOMMENDATION_STATE_LABELS: Record<
  CloudRecommendationState,
  string
> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  in_progress: 'In Progress',
  actioned: 'Actioned',
  dismissed: 'Dismissed',
};

/**
 * Mirrors the server-side state machine so the UI only offers legal moves.
 * The server validates independently — this is convenience, not enforcement.
 */
export const RECOMMENDATION_TRANSITIONS: Record<
  CloudRecommendationState,
  CloudRecommendationState[]
> = {
  open: ['acknowledged', 'in_progress', 'dismissed'],
  acknowledged: ['in_progress', 'dismissed'],
  in_progress: ['actioned', 'dismissed'],
  actioned: [],
  dismissed: [],
};

export interface CloudRecommendation {
  id: number;
  title: string;
  description?: string;
  category?: string;
  estimatedMonthlySaving?: number | string;
  currency: string;
  state: CloudRecommendationState;
  assignedToId?: number;
  assignedTo?: { id: number; firstName: string; lastName: string };
  dueDate?: string;
  dismissalReason?: string;
  createdBy?: { id: number; firstName: string; lastName: string };
  resources?: CloudResource[];
  createdAt: string;
  updatedAt: string;
}

/** Multi-select filters go over the wire as one comma-separated value. */
function toParams(query: CloudResourceQuery): Record<string, any> {
  const params: Record<string, any> = {};
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params[key] = Array.isArray(value) ? value.join(',') : value;
  });
  return params;
}

export const cloudAssetService = {
  async getResources(
    query: CloudResourceQuery = {},
    signal?: AbortSignal,
  ): Promise<{ data: CloudResource[]; total: number }> {
    const response = await apiClient.get('/cloud-resources', {
      params: toParams(query),
      signal,
    });
    return response.data;
  },

  async getStatistics(signal?: AbortSignal): Promise<CloudStatistics> {
    const response = await apiClient.get('/cloud-resources/statistics', {
      signal,
    });
    return response.data;
  },

  async getResource(id: number): Promise<CloudResource> {
    const response = await apiClient.get(`/cloud-resources/${id}`);
    return response.data;
  },

  async getResourceHistory(id: number): Promise<CloudResourceHistoryEntry[]> {
    const response = await apiClient.get(`/cloud-resources/${id}/history`);
    return response.data;
  },

  async createResource(data: Partial<CloudResource>): Promise<CloudResource> {
    const response = await apiClient.post('/cloud-resources', data);
    return response.data;
  },

  async updateResource(
    id: number,
    data: Partial<CloudResource>,
  ): Promise<CloudResource> {
    const response = await apiClient.put(`/cloud-resources/${id}`, data);
    return response.data;
  },

  /** Delete requires a reason (User Story 113) — it lands in the resource's
   * history, since a deleted resource can no longer explain itself. */
  async deleteResource(id: number, reason: string): Promise<void> {
    await apiClient.delete(`/cloud-resources/${id}`, { data: { reason } });
  },

  /** Every row in scope, for a client-side CSV export. */
  async getResourcesForExport(
    query: CloudResourceQuery = {},
  ): Promise<CloudResource[]> {
    const response = await apiClient.get('/cloud-resources/export', {
      params: toParams(query),
    });
    return response.data;
  },

  async getEnvironments(): Promise<CloudEnvironment[]> {
    const response = await apiClient.get('/cloud-environments');
    return response.data;
  },

  async createEnvironment(
    data: Partial<CloudEnvironment>,
  ): Promise<CloudEnvironment> {
    const response = await apiClient.post('/cloud-environments', data);
    return response.data;
  },

  async updateEnvironment(
    id: number,
    data: Partial<CloudEnvironment>,
  ): Promise<CloudEnvironment> {
    const response = await apiClient.put(`/cloud-environments/${id}`, data);
    return response.data;
  },

  async deleteEnvironment(id: number): Promise<void> {
    await apiClient.delete(`/cloud-environments/${id}`);
  },

  async getProviders(includeInactive = false): Promise<CloudProviderOption[]> {
    const response = await apiClient.get('/cloud-setup/providers', {
      params: includeInactive ? { includeInactive: 'true' } : {},
    });
    return response.data;
  },

  async createProvider(
    data: Partial<CloudProviderOption>,
  ): Promise<CloudProviderOption> {
    const response = await apiClient.post('/cloud-setup/providers', data);
    return response.data;
  },

  async updateProvider(
    id: number,
    data: Partial<CloudProviderOption>,
  ): Promise<CloudProviderOption> {
    const response = await apiClient.put(`/cloud-setup/providers/${id}`, data);
    return response.data;
  },

  async deleteProvider(id: number): Promise<void> {
    await apiClient.delete(`/cloud-setup/providers/${id}`);
  },

  async getResourceTypes(includeInactive = false): Promise<CloudResourceType[]> {
    const response = await apiClient.get('/cloud-setup/resource-types', {
      params: includeInactive ? { includeInactive: 'true' } : {},
    });
    return response.data;
  },

  async createResourceType(
    data: Partial<CloudResourceType>,
  ): Promise<CloudResourceType> {
    const response = await apiClient.post('/cloud-setup/resource-types', data);
    return response.data;
  },

  async updateResourceType(
    id: number,
    data: Partial<CloudResourceType>,
  ): Promise<CloudResourceType> {
    const response = await apiClient.put(
      `/cloud-setup/resource-types/${id}`,
      data,
    );
    return response.data;
  },

  async deleteResourceType(id: number): Promise<void> {
    await apiClient.delete(`/cloud-setup/resource-types/${id}`);
  },

  async getTiers(includeInactive = false): Promise<CloudTier[]> {
    const response = await apiClient.get('/cloud-setup/tiers', {
      params: includeInactive ? { includeInactive: 'true' } : {},
    });
    return response.data;
  },

  async createTier(data: Partial<CloudTier>): Promise<CloudTier> {
    const response = await apiClient.post('/cloud-setup/tiers', data);
    return response.data;
  },

  async updateTier(id: number, data: Partial<CloudTier>): Promise<CloudTier> {
    const response = await apiClient.put(`/cloud-setup/tiers/${id}`, data);
    return response.data;
  },

  async deleteTier(id: number): Promise<void> {
    await apiClient.delete(`/cloud-setup/tiers/${id}`);
  },

  async getLifecycleStatuses(): Promise<
    { id: number; label: string; value: string; description?: string }[]
  > {
    const response = await apiClient.get('/cloud-setup/lifecycle-statuses');
    return response.data;
  },

  async downloadImportTemplate(): Promise<Blob> {
    const response = await apiClient.get('/cloud-resources/import/template', {
      responseType: 'blob',
    });
    return response.data;
  },

  async validateImport(file: File): Promise<CloudImportValidation> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      '/cloud-resources/import/validate',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  async confirmImport(
    rows: any[],
  ): Promise<{ created: number; updated: number }> {
    const response = await apiClient.post('/cloud-resources/import/confirm', {
      rows,
    });
    return response.data;
  },

  // ─── Phase 2: insights ────────────────────────────────────────────────────

  async getOverview(): Promise<CloudOverview> {
    const response = await apiClient.get('/cloud-insights/overview');
    return response.data;
  },

  async getCostSummary(): Promise<CloudCostSummary> {
    const response = await apiClient.get('/cloud-insights/cost');
    return response.data;
  },

  async getAuditReport(): Promise<CloudAuditReport> {
    const response = await apiClient.get('/cloud-insights/audit');
    return response.data;
  },

  /** Analytics → Cloud tab (Story 98). Takes a signal: the tab re-fetches on
      refresh and on unmount mid-flight, like every other Analytics panel. */
  async getAnalytics(signal?: AbortSignal): Promise<CloudAnalytics> {
    const response = await apiClient.get('/cloud-insights/analytics', {
      signal,
    });
    return response.data;
  },

  // ─── Phase 2: recommendations ─────────────────────────────────────────────

  async getRecommendations(state?: string): Promise<CloudRecommendation[]> {
    const response = await apiClient.get('/cloud-recommendations', {
      params: state ? { state } : {},
    });
    return response.data;
  },

  async getRecommendationSummary(): Promise<Record<string, number>> {
    const response = await apiClient.get('/cloud-recommendations/summary');
    return response.data;
  },

  async createRecommendation(
    data: Partial<CloudRecommendation> & { resourceIds?: number[] },
  ): Promise<CloudRecommendation> {
    const response = await apiClient.post('/cloud-recommendations', data);
    return response.data;
  },

  async updateRecommendation(
    id: number,
    data: Partial<CloudRecommendation> & { resourceIds?: number[] },
  ): Promise<CloudRecommendation> {
    const response = await apiClient.put(`/cloud-recommendations/${id}`, data);
    return response.data;
  },

  /** State changes have their own endpoint — the server re-validates the move. */
  async transitionRecommendation(
    id: number,
    state: CloudRecommendationState,
    dismissalReason?: string,
  ): Promise<CloudRecommendation> {
    const response = await apiClient.patch(
      `/cloud-recommendations/${id}/state`,
      { state, dismissalReason },
    );
    return response.data;
  },

  async deleteRecommendation(id: number): Promise<void> {
    await apiClient.delete(`/cloud-recommendations/${id}`);
  },
};

export default cloudAssetService;
